// src/contexts/ThemeContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

// Create theme context
const ThemeContext = createContext({
  themeMode: 'light',
  toggleTheme: () => {},
  isThemeReady: false
});

// Define light and dark themes with improved consistency
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#9c27b0',
      light: '#ba68c8',
      dark: '#7b1fa2',
      contrastText: '#ffffff',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    success: {
      main: '#2e7d32',
      light: '#4caf50',
      dark: '#1b5e20',
    },
    error: {
      main: '#d32f2f',
      light: '#ef5350',
      dark: '#c62828',
    },
    warning: {
      main: '#f59e0b',      // amber-500
      light: '#fbbf24',     // amber-400
      dark: '#d97706',      // amber-600
      contrastText: '#ffffff',
    },
    info: {
      main: '#3b82f6',      // blue-500
      light: '#60a5fa',     // blue-400
      dark: '#2563eb',      // blue-600
      contrastText: '#ffffff',
    },
    divider: 'rgba(0, 0, 0, 0.12)',
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.6)',
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          transition: 'background-color 0.3s ease-in-out, color 0.3s ease-in-out',
        },
        // Add special transitions for navigation elements
        '.MuiTypography-button, .MuiBottomNavigationAction-root, .MuiListItem-root': {
          transition: 'color 0.3s ease-in-out, background-color 0.3s ease-in-out, border-color 0.3s ease-in-out',
        },
        // Force navigation elements to use theme colors
        '.nav-item-active': {
          color: '#1976d2 !important',
          borderColor: '#1976d2 !important',
        },
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          borderRadius: 12,
          transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'background-color',
          // DO NOT modify margin or padding here
        },
      },
      defaultProps: {
        elevation: 1,
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          transition: 'background-color 0.3s ease-in-out, color 0.3s ease-in-out',
          // DO NOT modify margin or padding here
        },
      },
      defaultProps: {
        elevation: 1,
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          backgroundImage: 'none',
          transition: 'background-color 0.3s ease-in-out',
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'background-color',
          '& .Mui-selected': {
            color: '#1976d2 !important',
          },
        },
      },
    },
    // PRESERVE SPACING IN STACK COMPONENT
    MuiStack: {
      defaultProps: {
        useFlexGap: true, // Use gap instead of margins for better spacing control
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: '#f5f5f9',
          color: 'rgba(0, 0, 0, 0.87)',
          border: '1px solid #dadde9',
        }
      }
    },
  },
});

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
      light: '#e3f2fd',
      dark: '#42a5f5',
      contrastText: '#000000',
    },
    secondary: {
      main: '#ce93d8',
      light: '#f3e5f5',
      dark: '#ab47bc',
      contrastText: '#000000',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    success: {
      main: '#66bb6a',
      light: '#81c784',
      dark: '#388e3c',
    },
    error: {
      main: '#f44336',
      light: '#e57373',
      dark: '#d32f2f',
    },
    warning: {
      main: '#fbbf24',      // amber-400 (brighter for dark bg)
      light: '#fde68a',     // amber-200
      dark: '#f59e0b',      // amber-500
      contrastText: '#000000',
    },
    info: {
      main: '#60a5fa',      // blue-400
      light: '#93c5fd',     // blue-300
      dark: '#3b82f6',      // blue-500
      contrastText: '#000000',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'background-color, color',
        },
        // Add special transitions for navigation elements
        '.MuiTypography-button, .MuiBottomNavigationAction-root, .MuiListItem-root': {
          transition: 'color 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        // Force navigation elements to use theme colors
        '.nav-item-active': {
          color: '#e3f2fd !important',  // primary.light
          borderColor: '#e3f2fd !important',
        },
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(255, 255, 255, 0.05)',
          borderRadius: 12,
          border: '1px solid rgba(255, 255, 255, 0.12)',
          transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'background-color',
          // DO NOT modify margin or padding here
        },
      },
      defaultProps: {
        elevation: 1,
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'background-color',
          // DO NOT modify margin or padding here
        },
      },
      defaultProps: {
        elevation: 1,
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          backgroundImage: 'none',
          transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'background-color',
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'background-color',
          '& .Mui-selected': {
            color: '#e3f2fd !important',  // primary.light
          },
        },
      },
    },
    // PRESERVE SPACING IN STACK COMPONENT
    MuiStack: {
      defaultProps: {
        useFlexGap: true, // Use gap instead of margins for better spacing control
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: '#424242',
          color: '#ffffff',
          border: '1px solid #616161',
        }
      }
    },
  },
});

// Utility function to apply CSS variables
const applyCssVariables = (mode, theme) => {
  const root = document.documentElement;
  
  // Common variables for both themes
  const commonVars = {
    '--transition-duration': '0.3s',
    '--border-radius': '8px',
  };
  
  // Theme-specific variables
  const themeVars = mode === 'light' 
    ? {
        '--chart-bg-color': theme.palette.background.paper,
        '--chart-text-color': theme.palette.text.primary,
        '--chart-text-color-muted': theme.palette.text.secondary,
        '--chart-grid-color': theme.palette.divider,
        '--chart-label-color': theme.palette.text.primary,
        '--chart-reference-line-color': theme.palette.primary.main,
        '--primary-color': theme.palette.primary.main,
        '--primary-color-light': theme.palette.primary.light,
        '--primary-color-dark': theme.palette.primary.dark,
        '--nav-active-color': theme.palette.primary.main,
        '--nav-active-border-color': theme.palette.primary.main,
      }
    : {
        '--chart-bg-color': theme.palette.background.paper,
        '--chart-text-color': theme.palette.text.primary,
        '--chart-text-color-muted': theme.palette.text.secondary,
        '--chart-grid-color': theme.palette.divider,
        '--chart-label-color': theme.palette.text.primary,
        '--chart-reference-line-color': theme.palette.primary.light,
        '--primary-color': theme.palette.primary.main,
        '--primary-color-light': theme.palette.primary.light,
        '--primary-color-dark': theme.palette.primary.dark,
        '--nav-active-color': theme.palette.primary.light,
        '--nav-active-border-color': theme.palette.primary.light,
      };
  
  // Apply all variables
  Object.entries({ ...commonVars, ...themeVars }).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });
};

// Function to force-update DOM elements to match the current theme - NOW PRESERVES SPACING
const forceDomThemeUpdate = (mode, theme) => {
  try {
    // Update chart elements - ONLY change background/color properties, nothing else
    document.querySelectorAll('.recharts-wrapper, .recharts-surface').forEach(el => {
      el.style.setProperty('background-color', theme.palette.background.paper, 'important');
    });
    
    // Update card elements - ONLY change background/color properties, nothing else
    document.querySelectorAll('.MuiCard-root, .themed-card').forEach(el => {
      // ONLY change background color and text color, DO NOT modify margin, padding, or other spacing properties
      el.style.setProperty('background-color', theme.palette.background.paper, 'important');
      el.style.setProperty('color', theme.palette.text.primary, 'important');
    });
    
    // Update navigation elements - different colors for light/dark
    const activeNavColor = mode === 'light' 
      ? theme.palette.primary.main 
      : theme.palette.primary.light;
      
    document.querySelectorAll('.nav-item-active').forEach(el => {
      el.style.setProperty('color', activeNavColor, 'important');
      el.style.setProperty('border-color', activeNavColor, 'important');
    });
    
    // Update bottom navigation
    document.querySelectorAll('.MuiBottomNavigationAction-root.Mui-selected').forEach(el => {
      el.style.setProperty('color', activeNavColor, 'important');
    });
    
    // Update AppBar
    document.querySelectorAll('.MuiAppBar-root').forEach(el => {
      el.style.setProperty('background-color', theme.palette.background.paper, 'important');
    });
  } catch (error) {
    console.error('Error applying forced DOM theme updates:', error);
  }
};

// Theme provider component
export const ThemeProvider = ({ children }) => {
  // Get user's preferred theme from localStorage or system preference
  const getInitialTheme = () => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme;
    }
    // Default to dark mode
    return 'dark';
  };

  const [themeMode, setThemeMode] = useState(getInitialTheme);
  const [isThemeReady, setIsThemeReady] = useState(false);
  
  // Create a new theme instance on every mode change
  const theme = React.useMemo(() => {
    return themeMode === 'dark' 
      ? createTheme(darkTheme) 
      : createTheme(lightTheme);
  }, [themeMode]);

  // Enhanced toggle theme function with immediate background update
const toggleTheme = useCallback(() => {
  setThemeMode(prevMode => {
    const newMode = prevMode === 'light' ? 'dark' : 'light';
    localStorage.setItem('theme', newMode);
    
    // Persist to backend
    const token = localStorage.getItem('token');
    if (token) {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://soldikeeper-backend-production.up.railway.app/api';
      fetch(`${API_BASE_URL}/user-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ theme: newMode })
      }).catch(() => {});
    }
    
    // CRITICAL: Force immediate background update
    document.body.style.backgroundColor = newMode === 'dark' ? '#121212' : '#ffffff';
    
    // Immediately update critical elements without waiting for React
    if (newMode === 'dark') {
      document.body.style.setProperty('background-color', '#121212', 'important');
      document.documentElement.style.setProperty('background-color', '#121212', 'important');
      document.querySelectorAll('.MuiBox-root').forEach(el => {
        if (el.parentElement === document.body || el.parentElement.parentElement === document.body) {
          el.style.setProperty('background-color', '#121212', 'important');
        }
      });
    } else {
      document.body.style.setProperty('background-color', '#ffffff', 'important');
      document.documentElement.style.setProperty('background-color', '#ffffff', 'important');
      document.querySelectorAll('.MuiBox-root').forEach(el => {
        if (el.parentElement === document.body || el.parentElement.parentElement === document.body) {
          el.style.setProperty('background-color', '#ffffff', 'important');
        }
      });
    }
    
    // Set data-theme attribute for CSS targeting
    document.documentElement.setAttribute('data-theme', newMode);
    document.body.setAttribute('data-theme', newMode);
    
    // Additional CSS variables for immediate visual feedback
    const root = document.documentElement;
    if (newMode === 'dark') {
      root.style.setProperty('--app-bg-color', '#121212', 'important');
      root.style.setProperty('--chart-bg-color', '#1e1e1e', 'important');
    } else {
      root.style.setProperty('--app-bg-color', '#ffffff', 'important');
      root.style.setProperty('--chart-bg-color', '#ffffff', 'important');
    }
    
    return newMode;
  });
}, []);
  

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (!localStorage.getItem('theme')) {
        setThemeMode(mediaQuery.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Set global theme attributes when theme changes
  useEffect(() => {
    console.log('Theme changed to:', themeMode);
    
    // Mark the document with the current theme
    document.documentElement.setAttribute('data-theme', themeMode);
    document.body.setAttribute('data-theme', themeMode);
    
    // Apply CSS variables
    applyCssVariables(themeMode, theme);
    
    // Apply initial forced DOM updates
    forceDomThemeUpdate(themeMode, theme);
    
    // Apply updates again after components have had time to render
    const timerShort = setTimeout(() => {
      forceDomThemeUpdate(themeMode, theme);
    }, 50);
    
    const timerMedium = setTimeout(() => {
      forceDomThemeUpdate(themeMode, theme);
      setIsThemeReady(true);
    }, 250);
    
    return () => {
      clearTimeout(timerShort);
      clearTimeout(timerMedium);
    };
  }, [themeMode, theme]);

  return (
    <ThemeContext.Provider value={{ themeMode, toggleTheme, isThemeReady }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};